import React from 'react';
import { Box, Button, Collapse, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { differenceInDays, startOfDay } from 'date-fns';
import HotelIcon from '@mui/icons-material/Hotel';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import FlightIcon from '@mui/icons-material/Flight';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import EventIcon from '@mui/icons-material/Event';
import TrainIcon from '@mui/icons-material/Train';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import PlaceIcon from '@mui/icons-material/Place';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import { DOCUMENT, documentTextButtonSx } from '../constants/documentTheme';
import {
  formatDurationLabel,
  formatTime24,
  formatTimeGap,
  getCategoryEyebrow,
  getConfirmationRef,
  parseTransportData,
} from '../utils/itineraryDocumentUtils';
import { getCurrencySymbol } from '../utils/currencyUtils';
import {
  getFlightCityRouteLabel,
  getFlightLegRouteLabel,
  isFlightItineraryItem,
} from '../utils/flightRouteUtils';
import { isCarRentalItineraryItem } from '../utils/rentalRouteUtils';
import { getItineraryItemDayRange } from '../utils/itineraryDaySpan';
import { buildRoadTripDayRoute } from '../utils/roadTripDragUtils';
import { PARTICIPANT_STATUS_LABEL_KEYS } from '../utils/memberItineraryParticipants';
import MapLink from './MapLink';
import ItineraryItemThumb from './ItineraryItemThumb';

const sourceTagSx = {
  px: 0.75,
  py: 0.15,
  fontFamily: DOCUMENT.sans,
  fontSize: 10,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: DOCUMENT.ink2,
  border: `1px solid ${DOCUMENT.ruleHard}`,
  lineHeight: 1.35,
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
};

const categoryIconSx = { fontSize: 16, color: DOCUMENT.ivory };

function StopCategoryIcon({ item }) {
  if (item?._isRoadTripCard || isCarRentalItineraryItem(item)) return <DirectionsCarIcon sx={categoryIconSx} />;
  if (isFlightItineraryItem(item)) return <FlightIcon sx={categoryIconSx} />;
  const type = String(item?.transportation_type || '').toLowerCase();
  if (type === 'train') return <TrainIcon sx={categoryIconSx} />;
  if (type === 'bus') return <DirectionsBusIcon sx={categoryIconSx} />;
  if (item?.category === 'hotel') return <HotelIcon sx={categoryIconSx} />;
  if (item?.category === 'restaurant') return <RestaurantIcon sx={categoryIconSx} />;
  if (item?.category === 'event') return <EventIcon sx={categoryIconSx} />;
  if (item?.category === 'activity') return <DirectionsWalkIcon sx={categoryIconSx} />;
  return <PlaceIcon sx={categoryIconSx} />;
}

const ItineraryStop = ({
  item,
  isLast = false,
  nextItem = null,
  currentDayDate = null,
  expanded = false,
  onToggle,
  onEdit,
  onDelete,
  onMoveToPersonal,
  onOpenMaps,
  onOpenRoute,
  onAddNote,
  onChangePhoto,
  onToggleVisibility,
  pin,
  canEdit = false,
  canMoveToPersonal = false,
  highlighted = false,
  showGroupPlanTag = false,
}) => {
  const { t } = useTranslation();
  const startTime = formatTime24(item.start_datetime);
  const endTime = item.end_datetime ? formatTime24(item.end_datetime) : null;
  const duration = formatDurationLabel(item.start_datetime, item.end_datetime);
  const gap = !isLast && nextItem
    ? formatTimeGap(item.end_datetime || item.start_datetime, nextItem.start_datetime)
    : null;
  const transport = parseTransportData(item);
  const transportLegs = Array.isArray(transport.legs) ? transport.legs : [];
  const isMultiLegFlight = isFlightItineraryItem(item) && transportLegs.length > 1;
  const flightLegRows = isMultiLegFlight
    ? transportLegs.map((leg, index) => ({
        key: `${item.id}-leg-${index}`,
        label: t('itinerary.flightLegs.legNumber', { number: index + 1, defaultValue: `Leg ${index + 1}` }),
        route: getFlightLegRouteLabel(leg, index, transportLegs),
        number: [leg.airline, leg.flight_number].filter(Boolean).join(' ') || null,
        depart: formatTime24(leg.departure_time),
        arrive: formatTime24(leg.arrival_time),
      }))
    : [];
  const confirmation = getConfirmationRef(item);
  const isRoadTrip = Boolean(item._isRoadTripCard && item._roadTripData);
  const rtd = item._roadTripData;
  const eyebrow = getCategoryEyebrow(item, t);

  const dayRange = getItineraryItemDayRange(item.start_datetime, item.end_datetime);
  const formatDayOf = (current, total) => t('itinerary.dayOf', {
    current,
    total,
    defaultValue: `Day ${current} of ${total}`,
  });
  let dayIndicatorText = '';
  if (item._roadTripSegment) {
    dayIndicatorText = formatDayOf(item._roadTripSegment.number, item._roadTripSegment.total);
  } else if (dayRange && dayRange.daySpan > 1 && currentDayDate) {
    const currentDayNumber = differenceInDays(startOfDay(currentDayDate), dayRange.firstDay) + 1;
    dayIndicatorText = formatDayOf(currentDayNumber, dayRange.daySpan);
  }

  const roadTripRoute = isRoadTrip ? buildRoadTripDayRoute(item._roadTripStops, item._roadTripPois) : [];

  const kvItems = [];
  if (duration) kvItems.push({ label: t('itinerary.duration'), value: duration });
  if (isRoadTrip) {
    if (rtd?.totalDistance) kvItems.push({ label: t('roadTrip.totalDistance'), value: rtd.totalDistance });
    if (rtd?.totalDuration) kvItems.push({ label: t('roadTrip.drivingTime'), value: rtd.totalDuration });
    kvItems.push({
      label: t('roadTrip.stops'),
      value: String((rtd?.orderedStops || []).length),
    });
  }
  if (item.cost && parseFloat(item.cost) > 0) {
    kvItems.push({
      label: t('itinerary.detailModal.cost'),
      value: `${getCurrencySymbol(item.cost_currency || 'USD')}${parseFloat(item.cost).toFixed(2)}`,
    });
  }
  if (item.creator_name) {
    kvItems.push({ label: t('itinerary.addedBy'), value: item.creator_name });
  }
  if (item.visibility === 'subgroup' && Array.isArray(item.participants) && item.participants.length > 0) {
    const participantNames = item.participants.map((participant) => {
      const name = participant.username || participant.name || participant.email;
      if (!name) return null;
      const statusKey = PARTICIPANT_STATUS_LABEL_KEYS[participant.status];
      return statusKey ? `${name} (${t(statusKey)})` : name;
    }).filter(Boolean);
    if (participantNames.length) {
      kvItems.push({
        label: t('activityModal.withLabel', { defaultValue: 'With' }),
        value: participantNames.join(', '),
      });
    }
  }
  if (confirmation) {
    kvItems.push({ label: t('itinerary.confirmation'), value: confirmation });
  }
  if (!isMultiLegFlight) {
    if (transport.airline) kvItems.push({ label: t('itinerary.transportFields.airline'), value: transport.airline });
    if (transport.flight_number) {
      kvItems.push({ label: t('itinerary.transportFields.flightNumber'), value: transport.flight_number });
    }
    if (transport.departure_airport || transport.origin) {
      kvItems.push({
        label: t('itinerary.departs'),
        value: transport.departure_airport || transport.origin,
      });
    }
    if (transport.arrival_airport || transport.destination) {
      kvItems.push({
        label: t('itinerary.arrives'),
        value: transport.arrival_airport || transport.destination,
      });
    }
  }
  if (transport.terminal) kvItems.push({ label: t('itinerary.transportFields.terminal'), value: transport.terminal });
  if (transport.gate) kvItems.push({ label: t('itinerary.transportFields.gate'), value: transport.gate });
  if (transport.seat) kvItems.push({ label: t('itinerary.transportFields.seat'), value: transport.seat });
  if (transport.car_type) kvItems.push({ label: t('itinerary.vehicle'), value: transport.car_type });

  const notes = Array.isArray(item.notes) ? item.notes : [];

  const glanceLines = [];
  if (item.category === 'hotel') {
    if (startTime) glanceLines.push(`${t('itinerary.checkIn')}: ${startTime}`);
    if (endTime) glanceLines.push(`${t('itinerary.checkOut')}: ${endTime}`);
  } else if (isRoadTrip && rtd?.orderedStops?.length) {
    glanceLines.push(rtd.orderedStops.map((stop) => stop.name).filter(Boolean).join(' → '));
  }
  if (item.location) glanceLines.push(item.location);

  return (
    <Box>
      <Box
        component="button"
        type="button"
        data-testid={`itinerary-stop-${item.id}`}
        id={`itinerary-stop-${item.id}`}
        aria-expanded={expanded}
        onClick={onToggle}
        sx={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: '44px 28px minmax(0, 1fr)',
          columnGap: { xs: 1, sm: 1.25 },
          alignItems: 'start',
          width: '100%',
          textAlign: 'left',
          font: 'inherit',
          color: 'inherit',
          background: 'none',
          border: 0,
          cursor: 'pointer',
          py: 1.1,
          px: 0,
          bgcolor: highlighted ? DOCUMENT.ivory : 'transparent',
          outline: highlighted ? `1px solid ${DOCUMENT.accent}` : 'none',
          outlineOffset: highlighted ? 4 : 0,
          '&:focus-visible': {
            outline: `1px solid ${DOCUMENT.accent}`,
            outlineOffset: 4,
          },
        }}
      >
        <Box sx={{ fontVariantNumeric: 'tabular-nums', pt: 0.35, minWidth: 0 }}>
          {startTime && item.category !== 'hotel' ? (
            <>
              <Typography sx={{ fontSize: 12, fontWeight: 500, color: DOCUMENT.ink, display: 'block', lineHeight: 1.2 }}>
                {startTime}
              </Typography>
              {endTime && endTime !== startTime && (
                <Typography sx={{ fontSize: 10.5, color: DOCUMENT.ink3, display: 'block', mt: 0.2 }}>
                  {endTime}
                </Typography>
              )}
            </>
          ) : item.category !== 'hotel' ? (
            <Typography sx={{ fontSize: 10.5, color: DOCUMENT.ink3, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {t('itinerary.openStop')}
            </Typography>
          ) : null}
        </Box>
        <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', pt: 0.15, minHeight: 28 }}>
          <Box
            data-testid={`itinerary-stop-icon-${item.id}`}
            sx={{
              width: 26,
              height: 26,
              borderRadius: '50%',
              bgcolor: DOCUMENT.ink2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
              flexShrink: 0,
            }}
          >
            <StopCategoryIcon item={item} />
          </Box>
          {!isLast && (
            <Box
              sx={{
                position: 'absolute',
                top: 28,
                bottom: -12,
                left: '50%',
                width: 2,
                bgcolor: DOCUMENT.ruleHard,
                transform: 'translateX(-50%)',
              }}
            />
          )}
        </Box>
        <Box sx={{ minWidth: 0, pt: 0.1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 0.75, minWidth: 0 }}>
            <Typography
              component="h4"
              sx={{
                fontFamily: DOCUMENT.sans,
                fontSize: { xs: 14.5, sm: 15.5 },
                fontWeight: 600,
                lineHeight: 1.25,
                color: DOCUMENT.ink,
                minWidth: 0,
                flex: '1 1 8rem',
                whiteSpace: 'normal',
              }}
            >
              {item._roadTripTitle || (isFlightItineraryItem(item) && getFlightCityRouteLabel(item)) || item.name}
            </Typography>
            {(item.client_visible === 0 || item.client_visible === false) && (
              <Typography
                component="span"
                data-testid={`itinerary-draft-${item.id}`}
                sx={{
                  ...sourceTagSx,
                  color: DOCUMENT.oxblood,
                  border: `1px solid ${DOCUMENT.oxblood}`,
                }}
              >
                {t('itinerary.draft')}
              </Typography>
            )}
            {typeof item.description === 'string' && item.description.includes('Source: email import') && (
              <Typography
                component="span"
                data-testid={`itinerary-email-import-${item.id}`}
                sx={sourceTagSx}
              >
                {t('emailForward.importedChip', 'Imported from email')}
              </Typography>
            )}
            {item.item_source === 'traveler' && item.traveler_name && (
              <Typography
                component="span"
                data-testid={`itinerary-traveler-${item.id}`}
                sx={sourceTagSx}
              >
                {t('itinerary.travelerItem', { name: item.traveler_name })}
              </Typography>
            )}
            {(item.item_source === 'member_personal' || item.visibility === 'personal') && (
              <Typography
                component="span"
                data-testid={`itinerary-personal-${item.id}`}
                sx={sourceTagSx}
              >
                {item.creator_name
                  ? t('tripShare.personalItemBy', { name: item.creator_name })
                  : t('tripShare.personalItem')}
              </Typography>
            )}
            {(item.item_source === 'member_subgroup' || item.visibility === 'subgroup') && (
              <Typography
                component="span"
                data-testid={`itinerary-subgroup-${item.id}`}
                sx={sourceTagSx}
              >
                {item.subgroup_name
                  ? t('tripShare.subgroupItemNamed', { name: item.subgroup_name })
                  : t('tripShare.subgroupItem')}
              </Typography>
            )}
            {showGroupPlanTag && (
              <Typography
                component="span"
                data-testid={`itinerary-group-plan-${item.id}`}
                sx={sourceTagSx}
              >
                {t('itinerary.groupPlanItem')}
              </Typography>
            )}
          </Box>
          {dayIndicatorText && (
            <Typography sx={{ fontSize: 11, color: DOCUMENT.accent, letterSpacing: '0.04em', mt: 0.15 }}>
              {eyebrow} · {dayIndicatorText}
            </Typography>
          )}
          {item.category === 'hotel' ? (
            glanceLines.map((line) => (
              <Typography key={line} sx={{ fontSize: 12.5, color: DOCUMENT.ink2, mt: 0.15, lineHeight: 1.35 }}>
                {line}
              </Typography>
            ))
          ) : (
            item.location ? (
              <Box sx={{ display: 'block', mt: 0.15, minWidth: 0, pr: 0.5 }}>
                <MapLink
                  item={item}
                  fontSize={12.5}
                  iconSize={13}
                  sx={{ maxWidth: '100%', minWidth: 0 }}
                />
              </Box>
            ) : null
          )}
          {item.visibility === 'subgroup' && Array.isArray(item.participants) && item.participants.length > 0 ? (
            <Box
              data-testid={`itinerary-participants-${item.id}`}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0.5,
                mt: 0.75,
                px: 0.85,
                py: 0.35,
                width: 'fit-content',
                maxWidth: '100%',
                bgcolor: `color-mix(in srgb, ${DOCUMENT.oxblood} 10%, ${DOCUMENT.surface})`,
                border: `1px solid color-mix(in srgb, ${DOCUMENT.oxblood} 28%, ${DOCUMENT.ruleHard})`,
                borderRadius: '2px',
              }}
            >
              <GroupOutlinedIcon sx={{ fontSize: 14, color: DOCUMENT.oxblood, flexShrink: 0, mt: '1px' }} />
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: DOCUMENT.oxblood,
                  lineHeight: 1.35,
                  letterSpacing: '0.01em',
                  minWidth: 0,
                  overflowWrap: 'anywhere',
                }}
              >
                {t('activityModal.participantsWith', {
                  names: item.participants.map((participant) => {
                    const name = participant.username || participant.name || participant.email;
                    const statusKey = PARTICIPANT_STATUS_LABEL_KEYS[participant.status];
                    return statusKey ? `${name} (${t(statusKey)})` : name;
                  }).filter(Boolean).join(', '),
                })}
              </Typography>
            </Box>
          ) : null}
        </Box>
      </Box>

      <Collapse in={expanded} timeout={320} unmountOnExit>
        <Box sx={{ pb: 2.5, pt: 0.25 }}>
          {isMultiLegFlight && (
            <Box sx={{ borderTop: `1px solid ${DOCUMENT.rule}`, pt: 1.5, mb: kvItems.length > 0 ? 2 : 0 }}>
              {flightLegRows.map((leg) => (
                <Box
                  key={leg.key}
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    flexWrap: 'wrap',
                    columnGap: 1.5,
                    py: 0.75,
                    borderBottom: `1px solid ${DOCUMENT.rule}`,
                  }}
                >
                  <Typography sx={{ fontSize: 10, letterSpacing: '0.19em', textTransform: 'uppercase', color: DOCUMENT.ink3, minWidth: 46 }}>
                    {leg.label}
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: DOCUMENT.ink, fontWeight: 500 }}>
                    {leg.route}
                  </Typography>
                  {leg.number && (
                    <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2 }}>
                      {leg.number}
                    </Typography>
                  )}
                  {(leg.depart || leg.arrive) && (
                    <Typography sx={{ ml: 'auto', fontSize: 13, color: DOCUMENT.ink3, fontVariantNumeric: 'tabular-nums' }}>
                      {leg.depart || '—'} – {leg.arrive || '—'}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
          {kvItems.length > 0 && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                columnGap: 4,
                borderTop: `1px solid ${DOCUMENT.rule}`,
                pt: 1.5,
              }}
            >
              {kvItems.map((row) => (
                <Box key={row.label} sx={{ py: 0.75, borderBottom: `1px solid ${DOCUMENT.rule}` }}>
                  <Typography sx={{ fontSize: 10, letterSpacing: '0.19em', textTransform: 'uppercase', color: DOCUMENT.ink3, mb: 0.5 }}>
                    {row.label}
                  </Typography>
                  <Typography sx={{ fontSize: 14, fontVariantNumeric: 'tabular-nums', color: DOCUMENT.ink }}>
                    {row.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          {roadTripRoute.length > 0 && (
            <Box component="ol" data-testid={`road-trip-route-${item.id}`} sx={{ listStyle: 'none', p: 0, m: 0, mt: 2 }}>
              {roadTripRoute.map(({ entry, isPlace }) => (
                <Box
                  component="li"
                  key={entry.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 1.5,
                    py: 0.75,
                    pl: isPlace ? 3 : 0,
                    borderBottom: `1px solid ${DOCUMENT.rule}`,
                  }}
                >
                  <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, minWidth: 42, fontVariantNumeric: 'tabular-nums' }}>
                    {formatTime24(entry.start_datetime) || '—'}
                  </Typography>
                  <Typography sx={{ fontSize: isPlace ? 13.5 : 14, color: DOCUMENT.ink, fontWeight: isPlace ? 400 : 500 }}>
                    {entry.name}
                  </Typography>
                  {isPlace && (
                    <Typography sx={{ fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: DOCUMENT.accent }}>
                      {getCategoryEyebrow(entry, t)}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
          {item.description && (
            <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2, lineHeight: 1.68, mt: 2, maxWidth: '62ch' }}>
              {item.description}
            </Typography>
          )}
          {canEdit && onChangePhoto && (
            <Box sx={{ mt: 1.75, maxWidth: 120 }}>
              <ItineraryItemThumb
                item={item}
                pin={pin}
                alt={item._roadTripTitle || item.name}
                canEdit={canEdit}
                onChangePhoto={onChangePhoto}
                width="100%"
              />
            </Box>
          )}
          {notes.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              {notes.map((note) => (
                <Typography key={note.id || note.created_at} sx={{ fontSize: 13.5, color: DOCUMENT.ink2, lineHeight: 1.6, mt: 0.75 }}>
                  {note.note || note.content}
                  {note.username ? ` — ${note.username}` : ''}
                </Typography>
              ))}
            </Box>
          )}
          {isRoadTrip && (
            <Button
              onClick={(event) => {
                event.stopPropagation();
                onOpenRoute?.(item);
              }}
              sx={{
                mt: 1.75,
                background: 'none',
                border: `1px solid ${DOCUMENT.ruleHard}`,
                borderRadius: '2px',
                fontSize: 12,
                letterSpacing: '0.09em',
                textTransform: 'uppercase',
                color: DOCUMENT.ink2,
                px: 2,
                py: 1,
                '&:hover': {
                  borderColor: DOCUMENT.accent,
                  color: DOCUMENT.ink,
                  background: 'none',
                },
              }}
            >
              {t('itinerary.viewRouteOnMap')}
            </Button>
          )}
          <Box
            sx={{
              display: 'flex',
              gap: 2.5,
              mt: 2.5,
              pt: 1.75,
              borderTop: `1px solid ${DOCUMENT.rule}`,
              flexWrap: 'wrap',
            }}
            onClick={(event) => event.stopPropagation()}
          >
            {canEdit && onToggleVisibility && (
              <Button
                sx={documentTextButtonSx}
                onClick={() => onToggleVisibility(item.id, item.client_visible === 0 || item.client_visible === false)}
              >
                {(item.client_visible === 0 || item.client_visible === false)
                  ? t('itinerary.publish')
                  : t('itinerary.hideFromClient')}
              </Button>
            )}
            {canEdit && (
              <Button sx={documentTextButtonSx} onClick={() => onEdit?.(item)}>
                {t('common.edit')}
              </Button>
            )}
            {canMoveToPersonal && onMoveToPersonal && (
              <Button
                sx={documentTextButtonSx}
                onClick={() => onMoveToPersonal(item)}
                data-testid={`move-to-personal-${item.id}`}
              >
                {t('itinerary.detailModal.moveToPersonal')}
              </Button>
            )}
            {canEdit && (
              <Button sx={documentTextButtonSx} onClick={() => onAddNote?.(item)}>
                {t('itinerary.addNote')}
              </Button>
            )}
            {(item.location || item.google_map_link) && (
              <Button sx={documentTextButtonSx} onClick={() => onOpenMaps?.(item)}>
                {t('itinerary.openInMaps')}
              </Button>
            )}
            {canEdit && (
              <Button
                sx={{
                  ...documentTextButtonSx,
                  '&:hover': {
                    background: 'none',
                    color: '#8C2B22',
                    borderBottom: '1px solid #8C2B22',
                  },
                }}
                onClick={() => onDelete?.(item)}
              >
                {t('common.remove')}
              </Button>
            )}
          </Box>
        </Box>
      </Collapse>

      {gap && (
        <Box
          sx={{
            position: 'relative',
            py: 1.5,
            fontSize: 12,
            color: DOCUMENT.ink3,
            letterSpacing: '0.05em',
            '&::after': {
              display: 'none',
            },
          }}
        >
          <Box component="span" sx={{ fontWeight: 400, color: DOCUMENT.accent }}>
            {t('itinerary.freeTime', { duration: gap })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ItineraryStop;
